import React, { useContext } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import { Button } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/BasicIframeModalLauncherButton.less';

import { SatCoreRegister } from '../SatCoreRegistry';

import { DIALOG_NAMES } from '../managers/DialogManager';

import { scrollBrowserWindowToTop } from '../utils';
import BasicIframeModalLauncherService from '../services/BasicIframeModalLauncherService';

/**
 * BasicIframeModalLauncherButton - A button trigger for BasicIframeModal
 *
 * @description
 * A button component that opens BasicIframeModal with a specified external URL.
 *
 * @example
 * // Minimal example 1 - basic button launcher with predefined url
 * <BasicIframeModalLauncherButton
 *   url='https://www.example.com'
 *   buttonLabel={t('openExternalWebsite')}
 *   headerTitle={t('externalWebsite')}
 * />
 *
 * @example
 * // Minimal example 2 - basic button launcher via iframeModalLauncherObj
 * <BasicIframeModalLauncherButton
 *   iframeModalLauncherObj={iframeModalLauncherObj)}
 *   buttonLabel={t('gettingStartedModalLauncherButtonLabel')}
 *   headerTitle={t('gettingStartedModalHeaderTitle')}
 * />
 *
 * @example
 * // Maximum options example - fully customized
 * <BasicIframeModalLauncherButton
 *   url='https://codesandbox.io/embed/your-demo'
 *   buttonLabel={t('interactiveDemoButton')}
 *   headerTitle={t('interactiveDemoTitle')}
 *   initWidth={1400}
 *   initHeight={900}
 *   disabled={false}
 *   closeOnDimmerClick={false}
 *   closeOnEscape={false}
 *   iframeProps={{
 *     allow: 'accelerometer; camera; encrypted-media; geolocation; gyroscope; microphone',
 *     sandbox: 'allow-scripts allow-same-origin allow-forms',
 *     loading: 'lazy',
 *     referrerPolicy: 'no-referrer'
 *   }}
 *   onOpen={() => console.info('Modal opened')}
 *   onClose={() => console.info('Modal closed')}
 *   className='custom-launcher-premium'
 *   style={{ margin: '20px', padding: '10px' }}
 * />
 */
const BasicIframeModalLauncherButton = observer((props = {}) => {
  const {
    basic = false,
    buttonLabel,
    className = '',
    closeOnDimmerClick = false,
    closeOnEscape = true,
    disabled = false,
    headerTitle,
    iframeModalLauncherObj = {},
    iframeProps,
    initHeight,
    initWidth,
    onClose = () => {},
    onOpen = () => {},
    primary = true,
    style = {},
    t,
    top
  } = props;

  const {
    dialogManager
  } = useContext(MobXProviderContext);

  const handleLaunchModal = async () => {
    if (disabled) {
      return;
    }
    const url = props?.url || await BasicIframeModalLauncherService.generateIframeModalLauncherUrl(iframeModalLauncherObj);
    if (!url) {
      return;
    }
    scrollBrowserWindowToTop();

    dialogManager.setOpenDialog(DIALOG_NAMES.BASIC_IFRAME_MODAL, {
      closeOnDimmerClick,
      closeOnEscape,
      headerTitle,
      iframeProps,
      initHeight,
      initWidth,
      onClose: () => {
        dialogManager.closeDialog(DIALOG_NAMES.BASIC_IFRAME_MODAL);
        onClose();
      },
      onOpen: () => {
        onOpen();
      },
      top,
      url
    });
  };

  const buttonText = buttonLabel || t('openExternalContent');

  return (
    <div
      className={classNames('BasicIframeModalLauncherButton', className)}
      style={style}>
      <div className='basic-iframe-modal-launcher-button-wrapper'>
        <Button
          aria-label={buttonText}
          basic={basic}
          disabled={disabled}
          onClick={handleLaunchModal}
          primary={primary}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
});

export default BasicIframeModalLauncherButton;

SatCoreRegister('BasicIframeModalLauncherButton', BasicIframeModalLauncherButton);
